(() => {
  if (window.__formeCartDrawerLoaded) return;
  window.__formeCartDrawerLoaded = true;

  class FormeCartDrawer {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-cart-drawer-panel]');
      this.status = root.querySelector('[data-cart-drawer-status]');
      this.opener = null;
      this.request = null;
      this.bind();
    }

    bind() {
      document.addEventListener('click', event => this.onClick(event));
      document.addEventListener('keydown', event => this.onKeydown(event));
      document.addEventListener('submit', event => this.onSubmit(event));
      document.addEventListener('cart:refresh', () => this.refresh(true));
      this.root.addEventListener('change', event => {
        const input = event.target.closest('[data-cart-input]');
        if (input) this.changeLine(Number(input.dataset.cartLine), Math.max(0, Number(input.value) || 0));
      });
    }

    onClick(event) {
      const trigger = event.target.closest('[data-cart-trigger]');
      if (trigger) {
        if (trigger.dataset.cartBehavior === 'page') return;
        event.preventDefault();
        this.opener = trigger;
        this.open();
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close(true);
        return;
      }
      const quantity = event.target.closest('[data-cart-quantity]');
      if (quantity && this.root.contains(quantity)) this.changeLine(Number(quantity.dataset.cartLine), Number(quantity.dataset.cartQuantity));
    }

    onKeydown(event) {
      if (!this.root.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled])')].filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form || event.defaultPrevented || form.closest('.shopify-payment-button')) return;
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      if (submit?.disabled) return;
      submit?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error('Cart add failed');
        await this.refresh(true);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      } catch {
        this.announce(this.root.dataset.errorMessage);
      } finally {
        submit?.removeAttribute('aria-busy');
      }
    }

    open() {
      this.root.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      this.root.querySelector('.cart-drawer__close')?.focus();
    }

    close(returnFocus = false) {
      this.root.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      if (returnFocus) this.opener?.focus();
    }

    async changeLine(line, quantity) {
      if (!line || quantity < 0) return;
      this.setLoading(true);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
        if (!response.ok) throw new Error('Cart change failed');
        await this.refresh(false);
      } catch {
        this.announce(this.root.dataset.errorMessage);
      } finally {
        this.setLoading(false);
      }
    }

    async refresh(openAfter = false) {
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      this.setLoading(true);
      try {
        const sectionUrl = new URL(window.Shopify?.routes?.root || '/', location.origin);
        sectionUrl.searchParams.set('section_id', this.root.dataset.sectionId);
        const [sectionResponse, cartResponse] = await Promise.all([
          fetch(sectionUrl, { signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } }),
          fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        ]);
        if (!sectionResponse.ok || !cartResponse.ok) throw new Error('Cart refresh failed');
        const doc = new DOMParser().parseFromString(await sectionResponse.text(), 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer]');
        const cart = await cartResponse.json();
        if (!fresh) throw new Error('Cart drawer section missing');
        this.root.querySelector('[data-cart-drawer-content]').replaceWith(fresh.querySelector('[data-cart-drawer-content]'));
        this.root.querySelector('[data-cart-drawer-count]').textContent = fresh.querySelector('[data-cart-drawer-count]').textContent;
        document.querySelectorAll('[data-cart-count]').forEach(count => {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count === 0;
        });
        if (openAfter) this.open();
      } catch (error) {
        if (error.name !== 'AbortError') this.announce(this.root.dataset.errorMessage);
      } finally {
        if (this.request === controller) this.setLoading(false);
      }
    }

    setLoading(loading) { this.root.classList.toggle('is-loading', loading); }
    announce(message) { if (this.status) this.status.textContent = message; }
  }

  const initialize = (scope = document) => {
    const root = scope.querySelector('[data-cart-drawer]');
    if (root && !root.dataset.initialized) {
      root.dataset.initialized = 'true';
      window.__formeCartDrawer = new FormeCartDrawer(root);
    }
  };
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
